import React from "react";
import { useTheme } from "react-jss";
import { range } from "../../utils/lodash";
import { PaginatedTableProps } from "./PaginatedTable";
import ButtonGroup from "../shared/ButtonGroup";
import useStyles from "./TablePagination.styles";

interface PaginationProps {
    tableProps: PaginatedTableProps;
    rowsPerPage: number;
    currentPage: number;
    pageCount: number;
}

export function TablePagination(props: PaginationProps) {
    const { tableProps, rowsPerPage, currentPage, pageCount } = props;
    const { onChangePage: onPageChanged } = tableProps;

    const theme = useTheme();
    const classes = useStyles({ theme });

    // Calculate the range of pages to show
    const getPageRange = () => {
        // For small number of pages, show all except first and last (they're shown separately)
        if (pageCount <= 5) {
            return {
                startPage: 2,
                endPage: pageCount - 1,
                showStartEllipsis: false,
                showEndEllipsis: false
            };
        }

        // Calculate the range to show exactly 3 pages in the middle
        let startPage, endPage;

        if (currentPage <= 3) {
            // Near start: show pages 2,3,4
            startPage = 2;
            endPage = 4;
        } else if (currentPage >= pageCount - 2) {
            // Near end: show last 3 pages before the end
            startPage = pageCount - 3;
            endPage = pageCount - 1;
        } else {
            // In middle: show current page and one on each side
            startPage = currentPage - 1;
            endPage = currentPage + 1;
        }

        // Only show ellipsis if there are gaps
        const showStartEllipsis = startPage > 2;
        const showEndEllipsis = endPage < pageCount - 1;

        return {
            startPage,
            endPage,
            showStartEllipsis,
            showEndEllipsis
        };
    };

    const { startPage, endPage, showStartEllipsis, showEndEllipsis } = getPageRange();

    function navigateToPage(page: number) {
        if (page > pageCount) {
            navigateToPage(pageCount);
            return;
        }
        if (page < 1) {
            navigateToPage(1);
            return;
        }
        onPageChanged((page - 1) * rowsPerPage, rowsPerPage);
    }

    // Don't show middle pages if there are only 2 pages
    const showMiddlePages = pageCount > 2;

    return (
        <div className={classes.pagination}>
            <ButtonGroup size="small">
                <ButtonGroup.Button
                    icon="chevron-double-left"
                    disabled={currentPage <= 1}
                    onClick={() => navigateToPage(1)}
                    id={`${tableProps.id}-double-left`}
                    isPagination={true}
                />
                <ButtonGroup.Button
                    icon="chevron-left"
                    disabled={currentPage <= 1}
                    onClick={() => navigateToPage(currentPage - 1)}
                    id={`${tableProps.id}-chevron-left`}
                    isPagination={true}
                />
                {/* First page */}
                <ButtonGroup.Button
                    text="1"
                    active={currentPage === 1}
                    onClick={() => navigateToPage(1)}
                    id={`${tableProps.id}-1`}
                    isPagination={true}
                />
                {/* Start ellipsis */}
                {showMiddlePages && showStartEllipsis && (
                    <ButtonGroup.Button text="&hellip;" isEclipse={true} disabled />
                )}
                {/* Middle pages */}
                {showMiddlePages && range(startPage, endPage + 1).map((page) => (
                    <ButtonGroup.Button
                        key={page}
                        text={String(page)}
                        active={currentPage === page}
                        onClick={() => navigateToPage(page)}
                        id={`${tableProps.id}-${page}`}
                        isPagination={true}
                    />
                ))}
                {/* End ellipsis */}
                {showMiddlePages && showEndEllipsis && (
                    <ButtonGroup.Button text="&hellip;" isEclipse={true} disabled />
                )}
                {/* Last page */}
                {pageCount > 1 && (
                    <ButtonGroup.Button
                        text={String(pageCount)}
                        active={currentPage === pageCount}
                        onClick={() => navigateToPage(pageCount)}
                        id={`${tableProps.id}-${pageCount}`}
                        isPagination={true}
                    />
                )}
                <ButtonGroup.Button
                    icon="chevron-right"
                    disabled={currentPage >= pageCount}
                    onClick={() => navigateToPage(currentPage + 1)}
                    id={`${tableProps.id}-chevron-right`}
                    isPagination={true}
                />
                <ButtonGroup.Button
                    icon="chevron-double-right"
                    disabled={currentPage >= pageCount}
                    onClick={() => navigateToPage(pageCount)}
                    id={`${tableProps.id}-double-right`}
                    isPagination={true}
                />
            </ButtonGroup>
        </div>
    );
}
